import React from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Heading from '@amzn/meridian/heading';
import { MetricName } from '../dashboard/constants';
import { useFlowColors } from '../useFlowColors';
import { SupportPathMetrics } from './model';
import StatusBadge from './StatusBadge';
import MetricRow from './MetricRow';
import { processPathLabel, uniqueProcessPathKey } from './utils';

export interface SupportPathsSectionProps {
    supportPaths?: SupportPathMetrics[];
}

/**
 * Width of a support card, in CSS pixels.
 *
 * Deliberately narrower than a flow node card (FLOW_NODE_WIDTH, 236): a flow node stacks
 * `received` plus a row per metric, while a support path carries exactly one row —
 * headcount — so the same width would be mostly empty space.
 */
const SUPPORT_CARD_WIDTH = 168;

/**
 * One support path as a compact card, in the same visual language as a Volume Flow map node
 * card: the path name, the status badge, then the headcount row.
 *
 * Health drives a coloured left border and a background tint through the shared dashboard
 * colours (`useFlowColors`, Meridian theme tokens, so it follows dark mode) — this section does
 * not map status to colour itself — and the StatusBadge carries the same information as
 * text plus a glyph, so colour is never the only signal.
 *
 * The headcount row is a `MetricRow` keyed by HEADCOUNT rather than a hand-rolled row, so
 * the icon, the `actual / expected` formatting and the signed variance are identical to the
 * HC row on a flow card, from one implementation.
 *
 * `expectedValue` and `variancePercentage` are coalesced to `null` rather than left
 * undefined: a support path always has a planned headcount, so a payload missing one reads
 * as `— / —` — an unknown target — instead of silently collapsing to a single number the
 * way `received` (which genuinely has no target) does.
 *
 * The card is plain markup: no role, no tabindex, nothing to activate. There is nothing to
 * drill into from a support path, so it must not look like a control. Nothing about buffer
 * renders here — a support path carries headcount only on the flow map.
 *
 * `cardKey` is passed in rather than re-derived from `path`: it is the same value the
 * section used as this card's React key, so the card's testids and its React identity can
 * never disagree about which path this is.
 */
const SupportPathCard: React.FC<{ path: SupportPathMetrics; cardKey: string }> = ({
    path,
    cardKey: key,
}) => {
    const colors = useFlowColors();
    const headcount = path.headcountComparison;
    return (
        <div
            // Kept as `…-row-…`: this is the long-standing per-path hook the page-level test
            // already targets, and the restyle does not change which path it identifies.
            // Unique per card, because `key` is: an identity-less path gets `unknown-<n>`,
            // so two of them in one payload stay separately addressable.
            data-testid={`flow-map-support-row-${key}`}
            style={{
                width: SUPPORT_CARD_WIDTH,
                boxSizing: 'border-box',
                textAlign: 'left',
                background: colors.statusBackground(path.healthStatus),
                // Per-side rather than `border` + `borderLeft`, so the accent stripe and the
                // hairline never fight over the same shorthand.
                borderTop: `1px solid ${colors.hairline}`,
                borderRight: `1px solid ${colors.hairline}`,
                borderBottom: `1px solid ${colors.hairline}`,
                borderLeft: `4px solid ${colors.statusAccent(path.healthStatus)}`,
                borderRadius: 4,
                padding: '6px 8px',
            }}
        >
            <Column spacing="100">
                <Text type="b300">{processPathLabel(path.processPath)}</Text>
                <StatusBadge status={path.healthStatus} />
                <MetricRow
                    rowKey={MetricName.HEADCOUNT}
                    actualValue={headcount?.actualValue}
                    expectedValue={headcount?.expectedValue ?? null}
                    variancePercentage={headcount?.variancePercentage ?? null}
                    data-testid={`flow-map-support-hc-${key}`}
                />
            </Column>
        </div>
    );
};

/**
 * Support (indirect) paths — Waterspider, Unloaders — in their own section under the Volume
 * Flow map. (On the Andon cards view they appear in the Staffing section instead.)
 *
 * They are deliberately NOT flow-map nodes and never appear in a map column: they carry no
 * volume, only headcount. They render as a wrapping row of small
 * cards that match the map's node cards, so the two read as one design, with each support
 * card more compact because it has a single metric to show.
 *
 * Exactly the paths the service sends are rendered, in the order it sent them — nothing is
 * added, relabelled or reclassified here. Which paths are SUPPORT versus a second-pass flow
 * node is a backend classification, not something the UI decides.
 */
const SupportPathsSection: React.FC<SupportPathsSectionProps> = ({ supportPaths }) => {
    const paths = supportPaths ?? [];

    return (
        <Column spacing="300" data-testid="flow-map-support-section">
            <Column spacing="100">
                <Heading level={3} type="h300">Support paths</Heading>
                <Text type="b200" color="secondary">
                    Indirect, headcount-only paths. Not part of the volume flow.
                </Text>
            </Column>

            {paths.length === 0 ? (
                <Text type="b300" color="secondary" data-testid="flow-map-support-empty">
                    No support paths for this interval.
                </Text>
            ) : (
                <Row
                    spacing="300"
                    alignmentVertical="top"
                    widths="fit"
                    wrap="down"
                    data-testid="flow-map-support-cards"
                >
                    {/* The key is disambiguated by list position when the service sent no
                        identity for the path, because `processPathKey` collapses every such
                        path onto one constant. Without that, two identity-less support paths
                        would be one React element and one ambiguous testid. */}
                    {paths.map((path, index) => {
                        const cardKey = uniqueProcessPathKey(path.processPath, index);
                        return <SupportPathCard key={cardKey} cardKey={cardKey} path={path} />;
                    })}
                </Row>
            )}
        </Column>
    );
};

export default SupportPathsSection;
